import axios, { AxiosError } from 'axios';

import { authServiceClient } from 'config';
import { TypedDispatch, refreshToken, logOut } from 'state';
import { setError } from 'state/actions';

const ignoreErrorUrls = ['user-file', 'user-inspection', 'multipart/video/presign'];

// A connectivity drop fails many in-flight requests at once. Collapse that burst
// into a single toast within this window instead of one toast per request.
const CONNECTIVITY_TOAST_THROTTLE_MS = 5000;
let lastConnectivityToastAt = 0;

// No HTTP response = offline / network drop / timeout (ERR_NETWORK, ECONNABORTED),
// as opposed to a real 4xx/5xx server error (which has a response).
const isConnectivityError = (e: Error | AxiosError): boolean =>
  axios.isAxiosError(e) && !e.response && !axios.isCancel(e);

export const handleErrorServiceInterceptor = async (
  e: Error | AxiosError,
  dispatch: TypedDispatch,
  isVerbose?: boolean,
) => {
  const errorMsg: string | string[] = extractErrorMessage(e, isVerbose);
  // @ts-ignore
  const originalConfig = e.config;

  // @ts-ignore
  if (originalConfig.url !== '/auth/sign-in' && e.response) {
    // @ts-ignore
    // eslint-disable-next-line no-underscore-dangle
    if (e.response.status === 401 && !originalConfig._retry) {
      // eslint-disable-next-line no-underscore-dangle
      originalConfig._retry = true;
      try {
        const accessToken = await dispatch(refreshToken());

        originalConfig.headers.Authorization = `Bearer ${accessToken}`;

        return authServiceClient(originalConfig);
      } catch (_error) {
        return Promise.reject(_error);
      }
    }
    // @ts-ignore
    // eslint-disable-next-line no-underscore-dangle
    if (e.response.status === 401 && originalConfig._retry) {
      return dispatch(logOut());
    }
  }

  // Background/polling requests opt out of the user-facing error toast entirely
  // (401 refresh above still runs). Keeps a failed silent refresh quiet.
  if (originalConfig?.silentError) {
    return;
  }

  // Connectivity / no-response errors: collapse the burst of concurrent failures
  // into a single deduped toast instead of one per in-flight request.
  if (isConnectivityError(e)) {
    const now = Date.now();

    if (now - lastConnectivityToastAt > CONNECTIVITY_TOAST_THROTTLE_MS) {
      lastConnectivityToastAt = now;
      dispatch(setError('Connection lost — check your internet and try again.'));
    }

    return;
  }

  if (
    !originalConfig ||
    !ignoreErrorUrls.some(url => originalConfig.url?.includes(url))
  ) {
    if (originalConfig?.url?.includes('sign-in')) {
      dispatch(setError('Invalid email or password'));
    } else {
      dispatch(setError(errorMsg));
    }
  }
};

export const extractErrorMessage = (
  e: Error | AxiosError | unknown,
  isVerbose?: boolean,
): string | string[] => {
  const messages: string[] = [];

  if (axios.isCancel(e)) {
    return '';
  }

  if (axios.isAxiosError(e)) {
    const statusCode = e.response?.status;

    if (statusCode) {
      if (e.response?.data?.response?.message === 'Pending inspection not found') {
        return '';
      }
      if (e.response?.data?.response?.message) {
        messages.push(e.response.data.response.message);

        if (!isVerbose) return e.response.data.response.message;
      }
      if (e.response?.data?.message) {
        messages.push(e.response.data.message);

        if (!isVerbose) return e.response.data.message;
      }
    }

    if (isVerbose) {
      if (e.message) messages.push(e.message);

      if (e.config) {
        messages.push(
          `Request: ${e.config.method?.toUpperCase()} ${e.config.url}${
            e.config.params ? '?' : ''
          }${new URLSearchParams(e.config.params)}`,
        );
      }
    }
  } else if (e instanceof Error && e?.message) {
    return e.message;
  }

  if (messages) return messages;

  return ['Something went wrong'];
};
